import { useState, useCallback, useMemo } from 'react'
import { FileIcon } from './FileIcon'
import { recentFiles as defaultRecentFiles } from '@/utils/data'
import type { FilterTab, FileType, RecentFile } from '@/types'
import styles from './RecentFiles.module.css'

interface RecentFilesProps {
  prefillFiles?: RecentFile[]
}

function mergeRecentFiles(prefillFiles: RecentFile[]): RecentFile[] {
  if (!prefillFiles.length) {
    return defaultRecentFiles
  }

  const usedNames = new Set(prefillFiles.map((file) => file.name.toLowerCase()))
  const remainingDefaults = defaultRecentFiles.filter(
    (file) => !usedNames.has(file.name.toLowerCase()),
  )

  return [...prefillFiles, ...remainingDefaults].slice(0, 8)
}

const TABS: FilterTab[] = ['All', 'Word', 'Excel', 'PowerPoint', 'PDF', 'More']

const TAB_TYPE_MAP: Partial<Record<FilterTab, FileType>> = {
  Word: 'word',
  Excel: 'excel',
  PowerPoint: 'powerpoint',
  PDF: 'pdf',
}

type ViewMode = 'list' | 'grid'
type SortKey = 'name' | 'opened' | 'owner'

export function RecentFiles({ prefillFiles = [] }: RecentFilesProps) {
  const [activeTab, setActiveTab] = useState<FilterTab>('All')
  const [filterText, setFilterText] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [sortKey, setSortKey] = useState<SortKey>('opened')
  const [sortAsc, setSortAsc] = useState(false)
  const recentFiles = useMemo(() => mergeRecentFiles(prefillFiles), [prefillFiles])

  const cycleSort = useCallback((key: SortKey) => {
    if (sortKey === key) {
      setSortAsc((prev) => !prev)
    } else {
      setSortKey(key)
      setSortAsc(true)
    }
  }, [sortKey])

  const filtered = recentFiles
    .filter((file) => {
      const matchesTab =
        activeTab === 'All' || activeTab === 'More' ||
        file.fileType === TAB_TYPE_MAP[activeTab]
      const matchesText =
        file.name.toLowerCase().includes(filterText.toLowerCase()) ||
        file.owner.toLowerCase().includes(filterText.toLowerCase())
      return matchesTab && matchesText
    })
    .sort((a, b) => {
      const av = sortKey === 'name' ? a.name : sortKey === 'owner' ? a.owner : a.opened
      const bv = sortKey === 'name' ? b.name : sortKey === 'owner' ? b.owner : b.opened
      return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av)
    })

  return (
    <section className={styles.section}>
      <div className={styles.toolbar}>
        <div className={styles.tabs} role="tablist">
          <span className={styles.recentLabel}>Recent</span>
          {TABS.map((tab) => (
            <button
              key={tab}
              role="tab"
              aria-selected={activeTab === tab}
              className={`${styles.tab} ${activeTab === tab ? styles.activeTab : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab !== 'All' && tab !== 'More' && (
                <FileTypeIcon type={TAB_TYPE_MAP[tab]!} />
              )}
              {tab}
            </button>
          ))}
        </div>
        <div className={styles.toolbarRight}>
          <input
            type="text"
            placeholder="Filter by name or person"
            className={styles.filterInput}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            aria-label="Filter files"
          />
          <button
            className={styles.sortBtn}
            onClick={() => cycleSort(sortKey)}
            title={`Sort by ${sortKey} (${sortAsc ? 'ascending' : 'descending'})`}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 4h10M4 7h6M6 10h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            Sort
          </button>
          <div className={styles.viewToggle}>
            <button
              className={`${styles.viewBtn} ${viewMode === 'list' ? styles.viewBtnActive : ''}`}
              onClick={() => setViewMode('list')}
              title="List view"
              aria-label="List view"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="3" width="12" height="2" rx="1" fill="currentColor" />
                <rect x="2" y="7" width="12" height="2" rx="1" fill="currentColor" />
                <rect x="2" y="11" width="12" height="2" rx="1" fill="currentColor" />
              </svg>
            </button>
            <button
              className={`${styles.viewBtn} ${viewMode === 'grid' ? styles.viewBtnActive : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid view"
              aria-label="Grid view"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="2" y="2" width="5" height="5" rx="1" fill="currentColor" />
                <rect x="9" y="2" width="5" height="5" rx="1" fill="currentColor" />
                <rect x="2" y="9" width="5" height="5" rx="1" fill="currentColor" />
                <rect x="9" y="9" width="5" height="5" rx="1" fill="currentColor" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <table className={styles.table} role="grid">
        <thead>
          <tr className={styles.headerRow}>
            <th className={`${styles.th} ${styles.checkboxTh}`} scope="col" aria-label="Select" />
            <th className={`${styles.th} ${styles.thSortable}`} scope="col" onClick={() => cycleSort('name')}>
              Name {sortKey === 'name' && <SortIndicator asc={sortAsc} />}
            </th>
            <th className={`${styles.th} ${styles.thSortable}`} scope="col" onClick={() => cycleSort('opened')}>
              Opened {sortKey === 'opened' && <SortIndicator asc={sortAsc} />}
            </th>
            <th className={`${styles.th} ${styles.thSortable}`} scope="col" onClick={() => cycleSort('owner')}>
              Owner {sortKey === 'owner' && <SortIndicator asc={sortAsc} />}
            </th>
            <th className={styles.th} scope="col">Activity</th>
            <th className={`${styles.th} ${styles.actionsTh}`} scope="col" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {filtered.map((file) => (
            <tr key={file.id} className={styles.row} tabIndex={0}>
              <td className={`${styles.td} ${styles.checkboxTd}`}>
                <input type="checkbox" className={styles.rowCheckbox} aria-label={`Select ${file.name}`} onClick={(e) => e.stopPropagation()} />
              </td>
              <td className={styles.td}>
                <div className={styles.nameCell}>
                  <FileIcon type={file.fileType} size="sm" />
                  <div className={styles.nameText}>
                    <span className={styles.fileName}>{file.name}</span>
                    <span className={styles.fileLocation}>{file.location}</span>
                  </div>
                </div>
              </td>
              <td className={styles.td}>
                <span className={styles.meta}>{file.opened}</span>
              </td>
              <td className={styles.td}>
                <span className={styles.meta}>{file.owner}</span>
              </td>
              <td className={styles.td}>
                <span className={styles.meta}>{file.activity ?? '—'}</span>
              </td>
              <td className={`${styles.td} ${styles.actionsTd}`}>
                <div className={styles.rowActions}>
                  <button className={styles.rowActionBtn} title="Share" aria-label="Share" onClick={(e) => e.stopPropagation()}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M11 5.5a2 2 0 1 0 0-3 2 2 0 0 0 0 3zM5 8.5a2 2 0 1 0 0-3 2 2 0 0 0 0 3zM11 13.5a2 2 0 1 0 0-3 2 2 0 0 0 0 3z" stroke="currentColor" strokeWidth="1.2" />
                      <path d="M7 7.2l2 1.1M9 7.7l-2 1.1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                    </svg>
                  </button>
                  <button className={styles.rowActionBtn} title="More options" aria-label="More options" onClick={(e) => e.stopPropagation()}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <circle cx="3" cy="8" r="1.2" />
                      <circle cx="8" cy="8" r="1.2" />
                      <circle cx="13" cy="8" r="1.2" />
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {filtered.length === 0 && (
        <div className={styles.empty}>No files match your filter.</div>
      )}
    </section>
  )
}

function SortIndicator({ asc }: { asc: boolean }) {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" style={{ marginLeft: 3, verticalAlign: 'middle' }}>
      {asc
        ? <path d="M5 2l4 6H1z" />
        : <path d="M5 8L1 2h8z" />}
    </svg>
  )
}

function FileTypeIcon({ type }: { type: FileType }) {
  const icons: Record<FileType, string> = {
    excel: 'https://res-1.public.onecdn.static.microsoft/files/fabric-cdn-prod_20260113.001/assets/brand-icons/product/svg/excel_20x1.svg',
    word: 'https://res-1.public.onecdn.static.microsoft/files/fabric-cdn-prod_20260113.001/assets/brand-icons/product/svg/word_20x1.svg',
    powerpoint: 'https://res-1.public.onecdn.static.microsoft/files/fabric-cdn-prod_20260113.001/assets/brand-icons/product/svg/powerpoint_20x1.svg',
    pdf: 'https://res-1.public.onecdn.static.microsoft/files/fabric-cdn-prod_20260113.001/assets/item-types/20/pdf.svg',
    folder: '#dcb67a',
  }

  const icon = icons[type]

  if (typeof icon === 'string' && icon.startsWith('http')) {
    return (
      <img
        src={icon}
        alt={type}
        style={{
          width: 16,
          height: 16,
          marginRight: 6,
          verticalAlign: 'middle',
          flexShrink: 0,
        }}
      />
    )
  }

  const colors: Record<string, string> = {
    folder: '#dcb67a',
  }

  return (
    <span
      style={{
        display: 'inline-block',
        width: 14,
        height: 14,
        borderRadius: 2,
        backgroundColor: colors[type],
        marginRight: 4,
        verticalAlign: 'middle',
        flexShrink: 0,
      }}
    />
  )
}
